import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import wemeetClient from '../wemeetClient';
import { MeetingStoreState, setNetWorkState } from '@store/meeting/meeting.slice';
import { getCameraStatus } from '@store/media/media.slice';
import { openDialog } from '@component/dialog';
import { omit } from 'lodash-es';
import { useUserMap } from './useUserList';
import { LocalVideoStatsMap, RemoteVideoStatsMap } from '@tencent/wemeet-websdk-next';
import { getBrowser, getOsName, execCommandCopy } from '@utils/browser';
import { setExtra, captureMessage } from '@utils/sentry';
import ISSUE_NAME from '@constant/issue-name';
import { openToast } from '@component/toast';

const videoRecord: Record<string, any> = {};
// 小于等于这个fps需要上报
const videoReportFps = 5;
// 采样多少次出问题才进行上报
const videoReportSampleCount = 5;
// 视频帧率fps下降的错误次数
let videoFpsErrorCount = 0;
// 上报的数组
let videoReportArr: any = [];

export const useNetWorkListener = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const userMap = useUserMap();
  const cameraStatus = useAppSelector(getCameraStatus);

  const qualityArr = [
    t('网络状况未知'),
    t('网络状况极佳'),
    t('网络状况较好'),
    t('网络状况一般'),
    t('网络状况差'),
    t('网络状况极差'),
    t('网络状况已断开'),
  ];
  const [remoteVideoObj, setRemoteVideoObj] = useState<Record<string, any>>({});

  const handleNetWorkState = async (event: {
    uplinkNetworkQuality: any;
    downlinkNetworkQuality?: any;
    uplinkLoss?: any;
    downlinkLoss?: any;
  }) => {
    const networkState: MeetingStoreState['networkState'] | {} = {};
    const { uplinkLoss, uplinkNetworkQuality, downlinkLoss } = event;
    if (event) {
      (networkState as MeetingStoreState['networkState']).currentlinkQuality =
        qualityArr[Math.max(event.uplinkNetworkQuality, event.downlinkNetworkQuality)];
      (networkState as MeetingStoreState['networkState']).level = Math.max(
        event.uplinkNetworkQuality,
        event.downlinkNetworkQuality,
      );
      (networkState as MeetingStoreState['networkState']).uplinkLoss = uplinkLoss;
      (networkState as MeetingStoreState['networkState']).downlinkLoss = downlinkLoss;
    }

    const transport = await wemeetClient.trtcClient.getTransportStats();

    if (transport) {
      (networkState as MeetingStoreState['networkState']).rtt = `${transport.rtt}ms`;
    }

    // https://trtc-1252463788.file.myqcloud.com/web/docs/tutorial-24-advanced-network-quality.html
    // 上行要是 3-5，而且开视频，进行弹窗提示
    if (uplinkNetworkQuality >= 3 && uplinkNetworkQuality <= 5 && cameraStatus) {
      openDialog({
        title: t('网络状态不佳'),
        dialogId: 'net-work',
        content: t('检测到您的网络状态不佳，为保证通话效果，建议关闭视频。'),
        btnConfigs: [
          {
            type: 'primary',
            text: t('关闭视频'),
            callback: () => {},
          },
          {
            text: t('忽略'),
          },
        ],
      });
    }
    dispatch(setNetWorkState(networkState));

    return {
      uplinkQuality: qualityArr[event.uplinkNetworkQuality],
      downlinkQuality: qualityArr[event.downlinkNetworkQuality],
      rtt: `${transport?.rtt ?? 0}ms`,
    };
  };

  const rePublishStream = async () => {
    try {
      wemeetClient.mediaCtrl.republishStream();
    } catch (error) {
      console.log('重新推流失败');
    }
  };

  const openFpsSlowDialog = (metaData: any, selfPureVideo: any) => {
    const title = `当前上传视频帧率明显下降到: ${selfPureVideo.framesEncoded} `;
    const content = `时间:${dayjs().format('YYYY/MM/DD HH:mm:ss')}\nurl:${location.href}\n视频信息：${JSON.stringify(
      omit(selfPureVideo, ['key']),
    )}`;

    openDialog({
      dialogId: 'fpsError',
      title,
      content,
      btnConfigs: [
        {
          type: 'primary',
          text: '请截图并复制信息联系adasi/damon',
          callback: () => {
            execCommandCopy(`${selfPureVideo.openId}${title}\n${content}\n网络元数据：${JSON.stringify(metaData)}`);
            openToast('信息已复制，准备发给adasi啦');
          },
        },
        {
          text: '重新推流',
          callback: () => {
            rePublishStream();
          },
        },
      ],
    });
  };

  const localVideoFpsDetect: (params: {
    framesEncodedFps: number;
    selfPureVideo: any;
    uplinkQuality: string;
    downlinkQuality: string;
    rtt: string;
  }) => void = async ({ framesEncodedFps, selfPureVideo, uplinkQuality, downlinkQuality, rtt }) => {
    if (framesEncodedFps <= videoReportFps && framesEncodedFps > 0) {
      // 帧率有问题，开始计算错误次数，videoReportSampleCount次错误采样后，直接上报错误
      if (videoFpsErrorCount < videoReportSampleCount) {
        const metaData = {
          selfPureVideo: omit(selfPureVideo, ['key']),
          remoteVideo: remoteVideoObj,
          uplinkQuality,
          downlinkQuality,
          rtt,
        };
        if (videoFpsErrorCount < videoReportSampleCount - 1) {
          videoReportArr.push({
            ...metaData,
          });
          videoFpsErrorCount += 1;
          return;
        }
        // 最后一次
        videoReportArr.push({
          ...metaData,
        });
        const { broswer, version } = getBrowser();

        setExtra('videoReportArr', videoReportArr);
        captureMessage(ISSUE_NAME.FPS_SLOW, 'videoStates', {
          info: `os:${getOsName()} browser:${broswer} version:${version} ${selfPureVideo.openId}帧率明显下降到： ${
            selfPureVideo.framesEncoded
          }`,
        });

        // 测试环境才弹窗，正式环境直接进行重新推流
        if (process.env.REACT_APP_ENV !== 'PROD') {
          openFpsSlowDialog(metaData, selfPureVideo);
        } else {
          await rePublishStream();
        }
      }
      videoFpsErrorCount += 1;
    } else {
      // 如果帧率恢复了，重新开始计算错误次数
      videoReportArr = [];
      videoFpsErrorCount = 0;
    }
  };
  /** 监听本地帧率 */
  // FIXME LocalVideoStatsMap 实际的数据比trtc里定义的多，
  const handleLocalVideo = (localVideo: LocalVideoStatsMap, spendSeconds: number) => {
    const selfVideoKey = Object.keys(localVideo)[0];
    // 这里一直有数据，framesEncoded不为0的时候再执行下面的逻辑

    const {
      bytesSent: oldBytesSent = 0,
      framesSent: oldFramesSent = 0,
      framesEncoded: oldFramesEncoded = 0,
    }: {
      bytesSent: number;
      framesSent: number;
      framesEncoded: number;
    } = videoRecord[selfVideoKey] || {};
    const {
      bytesSent,
      framesSent,
      framesEncoded,
    }: {
      bytesSent: number;
      framesSent: number;
      framesEncoded: number;
    } = localVideo[selfVideoKey];
    //
    const framesEncodedFps = Math.floor((framesEncoded - oldFramesEncoded) / spendSeconds);
    const framesSentFps = Math.floor((framesSent - oldFramesSent) / spendSeconds);
    const bytesSentKbs = Math.floor((((bytesSent - oldBytesSent) / 1024) * 1000) / spendSeconds) / 1000;
    const selfVideo = Object.keys(localVideo).reduce((total: any, key: string): any => {
      const userName = userMap[key] ? userMap[key].nickname : key;
      const tinyId = userMap[key] ? userMap[key].tinyId : '--';

      if (typeof videoRecord[key] === 'undefined') {
        total[key] = {
          bytesSent: '0KB/s',
          framesSent: '0fps',
          framesEncoded: `0fps`,
          key: `video-${key}`,
          openId: `${key}`,
          userName,
          tinyId: tinyId || '--',
        };
      } else {
        total[key] = {
          bytesSent: `${bytesSentKbs}KB/s`,
          framesSent: `${framesSentFps}fps`,
          framesEncoded: `${framesEncodedFps}fps`,
          key: `video-${key}`,
          // 新增openid字段
          openId: `${key}`,
          userName,
          tinyId: tinyId || '--',
        };
      }
      videoRecord[key] = { bytesSent, framesSent, framesEncoded };
      return total;
    }, {});

    return {
      selfVideo,
      selfPureVideo: selfVideo[selfVideoKey],
      framesEncodedFps,
    };
  };

  /** 监听远端帧率 */
  const handleRemoteVideo = (remoteVideoStats: RemoteVideoStatsMap, spendSeconds: number) => {
    const result = Object.keys(remoteVideoStats).reduce((total: any, key: string): any => {
      const userName = userMap[key] ? userMap[key].nickname : key;
      const tinyId = userMap[key] ? userMap[key].tinyId : '--';
      const {
        bytesReceived,
        framesDecoded,
        packetsLost,
      }: {
        bytesReceived: number;
        framesDecoded: number;
        packetsLost: number;
      } = remoteVideoStats[key];
      const {
        bytesReceived: oldBytesReceived = 0,
        packetsLost: oldPacketsLost = 0,
        framesDecoded: oldFramesDecoded = 0,
      }: {
        bytesReceived: number;
        framesDecoded: number;
        packetsLost: number;
      } = videoRecord[key] || {};
      let bytesReceivedKBs = 0;
      let framesDecodedFps = 0;
      if (typeof videoRecord[key] === 'undefined') {
        total[key] = {
          bytesReceived: `${bytesReceivedKBs}KB/s`,
          bytesReceivedKBsValue: bytesReceivedKBs,
          packetsLost: `0(${spendSeconds}s)`,
          framesDecoded: `${framesDecodedFps}fps`,
          key: `video-${key}`,
          userName,
          openId: `${key}`,
          tinyId: tinyId || '--',
        };
      } else {
        bytesReceivedKBs = Math.floor((((bytesReceived - oldBytesReceived) / 1024) * 1000) / spendSeconds) / 1000;
        framesDecodedFps = Math.floor((framesDecoded - oldFramesDecoded) / spendSeconds);
        total[key] = {
          bytesReceived: `${bytesReceivedKBs}KB/s`,
          bytesReceivedKBsValue: bytesReceivedKBs,
          packetsLost: `${packetsLost - oldPacketsLost}(${spendSeconds}s)`,
          framesDecoded: `${framesDecodedFps}fps`,
          key: `video-${key}`,
          openId: `${key}`,
          userName,
          tinyId: tinyId || '--',
        };
      }
      videoRecord[key] = { bytesReceived, framesDecoded, packetsLost };
      return total;
    }, {});
    setRemoteVideoObj(result);
  };
  useEffect(() => {
    let starTimestamp = new Date().getTime();
    const netWorkQuality = wemeetClient.onNetWorkQuality((event) => {
      (async () => {
        const { rtt, uplinkQuality, downlinkQuality } = await handleNetWorkState(event);
        const localVideoStats = await wemeetClient.trtcClient.getLocalVideoStats();
        const remoteVideoStats = await wemeetClient.trtcClient.getRemoteVideoStats();
        const endTimestamp = new Date().getTime();
        const spendSeconds = Math.max(1, Math.round((endTimestamp - starTimestamp) / 1000));
        let selfPureVideo;
        if (localVideoStats) {
          const selfVideoKey = Object.keys(localVideoStats)[0];
          if (localVideoStats[selfVideoKey].framesEncoded !== 0) {
            const { selfPureVideo: selfPureVideoBak, framesEncodedFps } = handleLocalVideo(
              localVideoStats,
              spendSeconds,
            );
            selfPureVideo = selfPureVideoBak;
            localVideoFpsDetect({
              selfPureVideo: selfPureVideoBak,
              framesEncodedFps,
              rtt,
              uplinkQuality,
              downlinkQuality,
            });
          }
        }

        if (remoteVideoStats) {
          handleRemoteVideo(remoteVideoStats, spendSeconds);
        }

        const remoteVideoDownloadSum = Object.keys(remoteVideoObj).reduce(
          (res: number, key: string) => res + remoteVideoObj[key].bytesReceivedKBsValue,
          0,
        );
        const metaData = {
          selfPureVideo: omit(selfPureVideo, ['key']),
          remoteVideo: selfPureVideo,
          uplinkQuality,
          downlinkQuality,
          rtt,
          remoteVideoDownloadSum: `${remoteVideoDownloadSum}KB/s`,
        };
        setExtra('metaData', metaData);

        starTimestamp = endTimestamp;
      })();
    });
    return () => {
      wemeetClient.removeListener(netWorkQuality);
    };
  }, []);
};
